import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Check, X, Zap, Crown, Rocket, Building2, ArrowLeft, 
  Calendar, Percent, BarChart3, Loader2, Sparkles, Mail, Phone, Building, User
} from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const planIcons = {
  starter: Zap,
  growth: Rocket,
  accelerator: Crown,
  enterprise: Building2
};

const planColors = {
  starter: 'from-slate-500 to-slate-600',
  growth: 'from-blue-500 to-blue-600',
  accelerator: 'from-purple-500 to-purple-600',
  enterprise: 'from-amber-500 to-amber-600'
};

const planBorders = {
  starter: 'border-slate-500/30',
  growth: 'border-blue-500/30',
  accelerator: 'border-purple-500/50 ring-2 ring-purple-500/20',
  enterprise: 'border-amber-500/30'
};

export default function SubscriptionPlans() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [cancelling, setCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  
  // Enterprise contact form state
  const [showEnterpriseForm, setShowEnterpriseForm] = useState(false);
  const [submittingEnterprise, setSubmittingEnterprise] = useState(false);
  const [enterpriseForm, setEnterpriseForm] = useState({
    name: '',
    organization: '',
    description: '',
    eventDate: '',
    eventTime: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions/plans`),
        axios.get(`${API_URL}/api/subscriptions/my-subscription`)
      ]);
      setPlans(plansRes.data.plans);
      setCurrentSubscription(subRes.data.subscription);
      setUsage(subRes.data.usage);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (tier) => {
    // For enterprise, show the contact form instead
    if (tier === 'enterprise') {
      setShowEnterpriseForm(true);
      return;
    }
    
    setUpgrading(tier);
    try {
      // Use the new recurring subscription endpoint
      const response = await axios.post(
        `${API_URL}/api/subscriptions/subscribe`,
        { tier, billing_cycle: billingCycle },
        
      );
      
      if (response.data.requires_payment && response.data.authorization_url) {
        // Redirect to Paystack checkout for recurring subscription
        toast.info(response.data.is_recurring 
          ? 'Setting up recurring billing...' 
          : 'Redirecting to payment...');
        window.location.href = response.data.authorization_url;
      } else {
        // Free tier - instant activation
        toast.success(response.data.message);
        await fetchData();
        await refreshUser();
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upgrade subscription');
      setUpgrading(null);
    }
  };

  const handleEnterpriseSubmit = async (e) => {
    e.preventDefault();
    
    if (!enterpriseForm.name || !enterpriseForm.organization || !enterpriseForm.email || !enterpriseForm.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmittingEnterprise(true);
    try {
      await axios.post(
        `${API_URL}/api/contact/enterprise`,
        enterpriseForm,
        
      );
      
      toast.success('Your inquiry has been sent! We\'ll get back to you soon.');
      setShowEnterpriseForm(false);
      setEnterpriseForm({
        name: '',
        organization: '',
        description: '',
        eventDate: '',
        eventTime: '',
        email: '',
        phone: ''
      });
    } catch (error) {
      console.error('Enterprise contact error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send inquiry. Please try again.');
    } finally {
      setSubmittingEnterprise(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/subscriptions/cancel`,
        { reason: cancelReason },
        
      );
      
      toast.success(response.data.message || 'Subscription cancelled');
      setShowCancelDialog(false);
      setCancelReason('');
      await fetchData();
      await refreshUser();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const formatPrice = (price, tier) => {
    if (tier === 'enterprise') return 'Custom';
    if (price === null || price === undefined) return 'Custom';
    if (price === 0) return 'Free';
    return `₦${price.toLocaleString()}`;
  };

  const currentTier = currentSubscription?.tier || 'starter';
  const tierOrder = ['starter', 'growth', 'accelerator', 'enterprise'];

  if (loading) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-10 w-64 skeleton rounded" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-96 skeleton rounded-2xl" />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Back button */}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Scale your events with the right subscription. Start free and upgrade as you grow.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 p-2 rounded-full bg-muted">
            <span className={`text-sm font-medium px-3 py-1 rounded-full transition-colors ${
              billingCycle === 'monthly' ? 'bg-background text-foreground' : 'text-muted-foreground'
            }`}>
              Monthly
            </span>
            <Switch
              checked={billingCycle === 'annually'}
              onCheckedChange={(checked) => setBillingCycle(checked ? 'annually' : 'monthly')}
              data-testid="billing-toggle"
            />
            <span className={`text-sm font-medium px-3 py-1 rounded-full transition-colors ${
              billingCycle === 'annually' ? 'bg-background text-foreground' : 'text-muted-foreground'
            }`}>
              Annually
              <Badge className="ml-2 bg-green-500/10 text-green-500 border-green-500/20">
                Save ~17%
              </Badge>
            </span>
          </div>
        </div>

        {/* Current Usage Card */}
        {usage && (
          <Card className="mb-8 bg-primary/5 border-primary/20">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold mb-1">Current Plan: {plans[currentTier]?.name || 'Starter'}</h3>
                  <p className="text-sm text-muted-foreground">
                    Events this month: {usage.events_used} / {usage.events_limit}
                  </p>
                </div>
                {usage.events_limit !== 'Unlimited' && (
                  <div className="w-full sm:w-64">
                    <Progress 
                      value={(usage.events_used / usage.events_limit) * 100} 
                      className="h-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1 text-right">
                      {usage.events_remaining} events remaining
                    </p>
                  </div>
                )}
                {/* Cancel button for paid plans */}
                {currentTier !== 'starter' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive border-destructive/30 hover:bg-destructive/10"
                    onClick={() => setShowCancelDialog(true)}
                    data-testid="cancel-subscription-btn"
                  >
                    Cancel Subscription
                  </Button>
                )}
              </div>
              {/* Show if it's a recurring subscription */}
              {currentSubscription?.is_recurring && (
                <p className="text-xs text-muted-foreground mt-2">
                  Auto-renews {currentSubscription.billing_cycle === 'annually' ? 'annually' : 'monthly'}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {tierOrder.map((tier) => {
            const plan = plans[tier];
            if (!plan) return null;
            
            // Hide starter plan for super_admin users
            if (tier === 'starter' && user?.admin_role === 'super_admin') return null;
            
            const Icon = planIcons[tier];
            const isCurrentPlan = currentTier === tier;
            const canUpgrade = tierOrder.indexOf(tier) > tierOrder.indexOf(currentTier);
            const canDowngrade = tierOrder.indexOf(tier) < tierOrder.indexOf(currentTier);
            const price = billingCycle === 'annually' ? plan.price_annually : plan.price_monthly;
            const isPopular = tier === 'accelerator';

            return (
              <Card 
                key={tier}
                className={`relative overflow-hidden transition-all hover:shadow-lg ${
                  isCurrentPlan ? 'ring-2 ring-primary' : ''
                } ${planBorders[tier]}`}
                data-testid={`plan-${tier}`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                
                {isCurrentPlan && (
                  <div className="absolute top-0 left-0 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-br-lg">
                    CURRENT
                  </div>
                )}

                <CardHeader className="text-center pt-8">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${planColors[tier]} flex items-center justify-center mx-auto mb-4`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.tagline}</CardDescription>
                </CardHeader>

                <CardContent className="text-center">
                  <div className="mb-6">
                    <span className="text-4xl font-bold">{formatPrice(price, tier)}</span>
                    {price !== null && price > 0 && (
                      <span className="text-muted-foreground">
                        /{billingCycle === 'annually' ? 'year' : 'month'}
                      </span>
                    )}
                  </div>

                  {/* Key Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
                    <div className="p-2 rounded-lg bg-muted/50">
                      <Calendar className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                      <div className="font-medium">
                        {plan.events_per_month === -1 ? 'Unlimited' : plan.events_per_month}
                      </div>
                      <div className="text-xs text-muted-foreground">events/mo</div>
                    </div>
                    <div className="p-2 rounded-lg bg-muted/50">
                      <Percent className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                      <div className="font-medium">{plan.ticketing_fee_percent}%</div>
                      <div className="text-xs text-muted-foreground">ticket fee</div>
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-2 text-left text-sm mb-6">
                    {plan.features.slice(0, 5).map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {plan.limits?.length > 0 && plan.limits.slice(0, 2).map((limit) => (
                      <li key={`limit-${limit}`} className="flex items-start gap-2 text-muted-foreground">
                        <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                        <span>{limit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter className="pt-0">
                  {isCurrentPlan ? (
                    <Button disabled className="w-full" variant="outline">
                      Current Plan
                    </Button>
                  ) : tier === 'enterprise' ? (
                    <Button
                      className="w-full bg-amber-600 hover:bg-amber-700"
                      onClick={() => setShowEnterpriseForm(true)}
                      data-testid="contact-enterprise"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Contact Us
                    </Button>
                  ) : canUpgrade ? (
                    <Button
                      className={`w-full ${isPopular ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                      onClick={() => handleUpgrade(tier)}
                      disabled={upgrading === tier}
                      data-testid={`upgrade-${tier}`}
                    >
                      {upgrading === tier ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Upgrade
                        </>
                      )}
                    </Button>
                  ) : canDowngrade ? (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => handleUpgrade(tier)}
                      disabled={upgrading === tier}
                      data-testid={`downgrade-${tier}`}
                    >
                      {upgrading === tier ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        'Downgrade'
                      )}
                    </Button>
                  ) : (
                    <Button className="w-full" onClick={() => handleUpgrade(tier)} data-testid={`select-${tier}`}>
                      Get Started
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* FAQ or Additional Info */}
        <Card className="bg-muted/30">
          <CardContent className="p-8">
            <h3 className="text-xl font-bold mb-4 text-center">Frequently Asked Questions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-2">Can I change my plan anytime?</h4>
                <p className="text-sm text-muted-foreground">
                  Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">What happens to my events if I downgrade?</h4>
                <p className="text-sm text-muted-foreground">
                  Your existing events remain active. You'll only be limited on creating new events.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Are there any hidden fees?</h4>
                <p className="text-sm text-muted-foreground">
                  No hidden fees. The only additional cost is the ticketing fee on paid tickets, which varies by plan.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Do you offer refunds?</h4>
                <p className="text-sm text-muted-foreground">
                  We offer a 7-day money-back guarantee for all paid plans. Contact support for assistance.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enterprise Contact Form Modal */}
      <Dialog open={showEnterpriseForm} onOpenChange={setShowEnterpriseForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-500" />
              Enterprise Inquiry
            </DialogTitle>
            <DialogDescription>
              Fill out this form and our team will get back to you within 24 hours.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleEnterpriseSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="ent-name">Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="ent-name"
                  placeholder="Your full name"
                  value={enterpriseForm.name}
                  onChange={(e) => setEnterpriseForm({...enterpriseForm, name: e.target.value})}
                  className="pl-10"
                  required
                  data-testid="enterprise-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="ent-org">Organization *</Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="ent-org"
                  placeholder="Company or organization name"
                  value={enterpriseForm.organization}
                  onChange={(e) => setEnterpriseForm({...enterpriseForm, organization: e.target.value})}
                  className="pl-10"
                  required
                  data-testid="enterprise-org"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="ent-desc">Short Description *</Label>
              <Textarea
                id="ent-desc"
                placeholder="Tell us about your event needs..."
                value={enterpriseForm.description}
                onChange={(e) => setEnterpriseForm({...enterpriseForm, description: e.target.value})}
                rows={3}
                required
                data-testid="enterprise-desc"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ent-date">Event Date (Optional)</Label>
                <Input
                  id="ent-date"
                  type="date"
                  value={enterpriseForm.eventDate}
                  onChange={(e) => setEnterpriseForm({...enterpriseForm, eventDate: e.target.value})}
                  data-testid="enterprise-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ent-time">Event Time (Optional)</Label>
                <Input
                  id="ent-time"
                  type="time"
                  value={enterpriseForm.eventTime}
                  onChange={(e) => setEnterpriseForm({...enterpriseForm, eventTime: e.target.value})}
                  data-testid="enterprise-time"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="ent-email">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="ent-email"
                  type="email"
                  placeholder="your@email.com"
                  value={enterpriseForm.email}
                  onChange={(e) => setEnterpriseForm({...enterpriseForm, email: e.target.value})}
                  className="pl-10"
                  required
                  data-testid="enterprise-email"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="ent-phone">Phone Number *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="ent-phone"
                  type="tel"
                  placeholder="+234 xxx xxx xxxx"
                  value={enterpriseForm.phone}
                  onChange={(e) => setEnterpriseForm({...enterpriseForm, phone: e.target.value})}
                  className="pl-10"
                  required
                  data-testid="enterprise-phone"
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEnterpriseForm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                disabled={submittingEnterprise}
                data-testid="submit-enterprise"
              >
                {submittingEnterprise ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Inquiry
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription? You will immediately lose access to your current plan features.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
              <p className="text-sm font-medium text-destructive">You will lose:</p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• Your {plans[currentTier]?.events_per_month || 0} events/month limit</li>
                <li>• Priority support</li>
                <li>• All premium features</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cancelReason">Reason for cancelling (optional)</Label>
              <Textarea
                id="cancelReason"
                placeholder="Help us improve by sharing why you're leaving..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              className="flex-1"
              disabled={cancelling}
            >
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              className="flex-1"
              disabled={cancelling}
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Cancel Now'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
